import { getCurrentDateLocal } from '@/utils/common';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  LessonSchedule,
  ViewType,
  Lesson,
  HoverState,
  InstructorRosterInstructor
} from '@/constants/Interfaces/CommonInterfaces';

export type InstructorRosterStoreState = {
  // Data
  selectedInstructor: null | number;
  lessonSchedule: LessonSchedule[];
  selectedLesson: Lesson | null;
  deletedRecord: Record<string, unknown> | null;

  // UI State
  selectedDate: string | null;
  selectedType: ViewType;
  showUndoModal: boolean;
  isLoading: boolean;
  error: string | null;

  // Hover State for Lesson Details Popup
  hoverState: HoverState;

  // Actions
  setSelectedInstructor: (data: number | null) => void;
  setLessonSchedule: (
    data: LessonSchedule[] | ((prev: LessonSchedule[]) => LessonSchedule[])
  ) => void;
  setDeletedRecord: (data: Record<string, unknown> | null) => void;
  setSelectedLesson: (lesson: Lesson | null) => void;
  setSelectedDate: (date: string | null) => void;
  setSelectedType: (type: ViewType) => void;
  setShowUndoModal: (show: boolean) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Mutations for incremental updates
  upsertLessonForInstructor: (
    instructorId: number,
    lesson: Lesson,
    scheduleGroupId?: number
  ) => void;

  // Hover Actions
  showLessonDetails: (
    lesson: Lesson,
    instructor: InstructorRosterInstructor,
    position: { x: number; y: number }
  ) => void;
  hideLessonDetails: () => void;
  setPopupHovering: (hovering: boolean) => void;

  resetStore: () => void;
};

type InstructorRosterDataState = {
  selectedInstructor: null | number;
  lessonSchedule: LessonSchedule[];
  selectedLesson: Lesson | null;
  deletedRecord: Record<string, unknown> | null;
  selectedDate: string | null;
  selectedType: ViewType;
  showUndoModal: boolean;
  isLoading: boolean;
  error: string | null;
  hoverState: HoverState;
};

const initialState: InstructorRosterDataState = {
  selectedInstructor: null,
  lessonSchedule: [],
  selectedLesson: null,
  selectedDate: getCurrentDateLocal(),
  deletedRecord: null,
  selectedType: 'Day',
  showUndoModal: false,
  isLoading: false,
  error: null,
  hoverState: {
    isVisible: false,
    lesson: null,
    instructor: null,
    position: { x: 0, y: 0 },
    isPopupHovering: false
  }
};

const useInstructorRosterStore = create<InstructorRosterStoreState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setSelectedInstructor: (data: number | null) =>
        set({ selectedInstructor: data }),
      setLessonSchedule: (
        data: LessonSchedule[] | ((prev: LessonSchedule[]) => LessonSchedule[])
      ) => {
        if (typeof data === 'function') {
          set(state => ({ lessonSchedule: data(state.lessonSchedule) }));
        } else {
          set({ lessonSchedule: data });
        }
      },

      setSelectedLesson: (lesson: Lesson | null) =>
        set({ selectedLesson: lesson }),

      setDeletedRecord: (data: Record<string, unknown> | null) =>
        set({ deletedRecord: data }),

      setSelectedDate: (date: string | null) => set({ selectedDate: date }),

      setSelectedType: (type: ViewType) => set({ selectedType: type }),

      setShowUndoModal: (show: boolean) => set({ showUndoModal: show }),

      setIsLoading: (loading: boolean) => set({ isLoading: loading }),

      setError: (error: string | null) => set({ error }),

      upsertLessonForInstructor: (
        instructorId: number,
        lesson: Lesson,
        scheduleGroupId?: number
      ) => {
        const current = get().lessonSchedule || [];

        // If the store currently holds a flat list of lessons, append/replace in-place
        const looksFlat = current.every((x: unknown) => {
          const obj = x as { start?: string; string?: string };
          return Boolean(obj?.start) || Boolean(obj?.string);
        });
        if (looksFlat) {
          let nextFlat = current as unknown as Lesson[];
          if (scheduleGroupId) {
            nextFlat = (nextFlat as Lesson[]).map(l => {
              const sgId = (
                l as unknown as { schedule_group?: { id?: number } }
              )?.schedule_group?.id;
              return sgId === scheduleGroupId ? (lesson as Lesson) : l;
            });
          } else {
            nextFlat = [...(nextFlat as Lesson[]), lesson as Lesson];
          }
          set({ lessonSchedule: nextFlat as unknown as LessonSchedule[] });
          return;
        }

        // Otherwise, assume instructor-bucketed structure
        let found = false;
        const nextBuckets = (current as LessonSchedule[]).map(item => {
          const itemId = (item as unknown as { id?: number })?.id;
          if (itemId !== instructorId) return item;
          found = true;
          const lessons = ((item as unknown as { lessons?: Lesson[] })
            ?.lessons || []) as Lesson[];
          if (scheduleGroupId) {
            const replaced = lessons.map(l => {
              const sgId = (
                l as unknown as { schedule_group?: { id?: number } }
              )?.schedule_group?.id;
              return sgId === scheduleGroupId ? (lesson as Lesson) : l;
            });
            return {
              ...(item as unknown as Record<string, unknown>),
              lessons: replaced
            } as LessonSchedule;
          }
          return {
            ...(item as unknown as Record<string, unknown>),
            lessons: [...lessons, lesson]
          } as LessonSchedule;
        });

        // If no bucket exists for this instructor, create one
        const createdIfMissing = found
          ? nextBuckets
          : [
              ...nextBuckets,
              {
                id: instructorId,
                slug: String(instructorId),
                name:
                  (lesson as unknown as { instructor?: { name?: string } })
                    ?.instructor?.name || 'Instructor',
                day_off: false,
                lessons: [lesson]
              } as unknown as LessonSchedule
            ];

        set({ lessonSchedule: createdIfMissing });
      },

      showLessonDetails: (
        lesson: Lesson,
        instructor: InstructorRosterInstructor,
        position: { x: number; y: number }
      ) => {
        // Only update if the state is actually different
        const currentState = get();
        const isSameLesson =
          currentState.hoverState.isVisible &&
          currentState.hoverState.lesson?.client_name === lesson.client_name &&
          currentState.hoverState.lesson?.start === lesson.start &&
          currentState.hoverState.instructor?.name === instructor.name;

        if (!isSameLesson) {
          set({
            hoverState: {
              isVisible: true,
              lesson,
              instructor,
              position,
              isPopupHovering: false
            }
          });
        }
      },

      hideLessonDetails: () => {
        // Only update if currently visible and not hovering the popup
        const currentState = get();
        if (
          currentState.hoverState.isVisible &&
          !currentState.hoverState.isPopupHovering
        ) {
          set({
            hoverState: {
              isVisible: false,
              lesson: null,
              instructor: null,
              position: { x: 0, y: 0 },
              isPopupHovering: false
            }
          });
        }
      },

      setPopupHovering: (hovering: boolean) => {
        const currentState = get();
        if (currentState.hoverState.isPopupHovering !== hovering) {
          set({
            hoverState: {
              ...currentState.hoverState,
              isPopupHovering: hovering
            }
          });
        }
      },

      resetStore: () => set(initialState)
    }),
    {
      name: 'instructor-roster-store'
    }
  )
);

export default useInstructorRosterStore;
